import React from 'react';
import {
    CommandBar,
    DetailsList,
    DetailsListLayoutMode,
    getTheme,
    IColumn,
    Icon,
    SelectionMode,
    Spinner,
    Text,
    TooltipHost
} from '@fluentui/react';
import {ICsvImportActivity, useGetCsvImportActivityQuery} from '../../store/endpoints/CsvImportEndpoints';

const TYPE_LABELS: {[type: string]: string} = {
    company: 'Companies',
    contact: 'Contacts',
    location: 'Locations',
};

/** Green check for a clean run; warning triangle (with the recorded errors as a tooltip) otherwise. */
const StatusIcon: React.FC<{activity: ICsvImportActivity}> = ({activity}) => {
    const theme = getTheme();
    if (activity.success) return <Icon iconName='CheckMark' styles={{root: {color: theme.palette.themePrimary}}} aria-label='Succeeded' />;
    return (
        <TooltipHost content={activity.errors?.length ? <ul style={{margin: 0, paddingLeft: '1.5em'}}>
            {activity.errors.map((error, index) => <li key={index}>{error}</li>)}
        </ul> : 'Completed with errors'}>
            <Icon iconName='Warning' styles={{root: {color: theme.palette.redDark}}} aria-label='Completed with errors' />
        </TooltipHost>
    );
};

/**
 * The Contact Management "Activity" pivot: the history of CSV imports for this organization — who ran
 * each one, when, and how many records were created/updated/skipped/errored.
 */
const ImportActivityDisplay: React.FC = () => {
    const activity = useGetCsvImportActivityQuery();
    const items = activity.data?.items ?? [];

    const columns: IColumn[] = [
        {
            key: 'status', name: '', minWidth: 24, maxWidth: 24,
            onRender: (item: ICsvImportActivity) => <StatusIcon activity={item} />
        },
        {
            key: 'started', name: 'Started', minWidth: 140, maxWidth: 170,
            onRender: (item: ICsvImportActivity) => item.started ? new Date(item.started).toLocaleString() : '-'
        },
        {
            key: 'username', name: 'Started By', minWidth: 160, maxWidth: 220,
            onRender: (item: ICsvImportActivity) => item.username ?? '-'
        },
        {
            key: 'importType', name: 'Type', minWidth: 80, maxWidth: 100,
            onRender: (item: ICsvImportActivity) => TYPE_LABELS[item.importType ?? ''] ?? item.importType ?? '-'
        },
        {
            key: 'fileName', name: 'File', minWidth: 160, maxWidth: 260,
            onRender: (item: ICsvImportActivity) => item.fileName ?? '-'
        },
        {
            key: 'referenceName', name: 'Reference', minWidth: 100, maxWidth: 160,
            onRender: (item: ICsvImportActivity) => item.referenceName ?? '-'
        },
        {key: 'rows', name: 'Rows', minWidth: 50, maxWidth: 70, fieldName: 'rows'},
        {key: 'created', name: 'Created', minWidth: 60, maxWidth: 80, fieldName: 'created'},
        {key: 'updated', name: 'Updated', minWidth: 60, maxWidth: 80, fieldName: 'updated'},
        {key: 'skipped', name: 'Skipped', minWidth: 60, maxWidth: 80, fieldName: 'skipped'},
        {
            key: 'errored', name: 'Errored', minWidth: 60, maxWidth: 80,
            onRender: (item: ICsvImportActivity) => item.errored > 0
                ? <TooltipHost content={item.errors?.length ? <ul style={{margin: 0, paddingLeft: '1.5em'}}>
                    {item.errors.map((error, index) => <li key={index}>{error}</li>)}
                </ul> : undefined}>
                    <Text styles={{root: {color: getTheme().palette.redDark, fontWeight: 600}}}>{item.errored}</Text>
                </TooltipHost>
                : <span>0</span>
        },
    ];

    return (
        <div>
            <CommandBar
                items={[]}
                farItems={[{
                    key: 'refresh',
                    text: 'Refresh',
                    iconProps: {iconName: 'Refresh'},
                    iconOnly: true,
                    onClick: () => { activity.refetch(); },
                }]}
            />
            {activity.isLoading && <Spinner label='Loading import activity…' />}
            {!activity.isLoading && items.length === 0 &&
                <Text>No imports have been run yet. Use Import → From CSV on the Companies or Contacts tab to get started.</Text>}
            {items.length > 0 &&
                <DetailsList
                    items={items}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                    compact
                />}
        </div>
    );
};

export default ImportActivityDisplay;
